<template>
  <div v-if="embedHtml" class="ao__cnc-simple-artobject" v-html="embedHtml"></div>
</template>

<script setup lang="ts">
import {onMounted} from "vue"
import loadEmbedScript from "~/server/utils/request/loadEmbedScript"

const {payload} = defineProps<{
  payload?: any
}>()

const embedHtml = ref<string | null>(null)

const embed = payload?.attributes?.embed
const providerName = embed?.provider_name

const templateListItem = useUniWebTemplate().getTemplateListItem()

// Render gate lives in useArticleEmbeds() so that this component and the embed
// counting in ArticleDetail.vue share one definition of "renders" instead of two
// copies that can drift apart.
//
// Note this is derived once at setup, not reactive: if Vue ever reused this
// instance for a different payload, the rendered HTML would go stale. Today the
// article pages destroy the tree between articles, so that path is not reachable
// — but a payload change under a live instance would not be picked up here.
if (useArticleEmbeds().isRenderableEmbed(payload, templateListItem)) {
  embedHtml.value = embed.html
}

const embedScripts: Record<string, { src: string; callback?: () => void }> = {
  Instagram: {
    src: 'https://www.instagram.com/embed.js',
    callback: () => { (window as any).instgrm?.Embeds?.process() },
  },
  TikTok: {
    src: 'https://www.tiktok.com/embed.js',
  },
  Twitter: {
    src: 'https://platform.twitter.com/widgets.js',
    callback: () => { (window as any).twttr?.widgets?.load() },
  },
  Pinterest: {
    src: 'https://assets.pinterest.com/js/pinit.js',
  },
}

onMounted(() => {
  if (!embedHtml.value || !providerName) return

  const scriptConfig = embedScripts[providerName]
  if (scriptConfig) {
    loadEmbedScript(
      providerName.toLowerCase(),
      scriptConfig.src,
      {},
      scriptConfig.callback ?? (() => {}),
    )
  }
})
</script>
